import React, { useState, useEffect, useRef } from "react";
import { formatStoryDate } from "../../../helper/date-utils";
import { object, bool } from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";

import FollowUs from "../follow-us";

import Authors from "../../../atoms/author";
import SocialMediaShare from "../../../molecules/social-media-share";

import "./author-and-timestamp.m.css";
import CommentsCount from "../../../atoms/comments-count";
import Share from "../../../atoms/other-icons/Share";
import useAdType from "../../../helper/useAdType";
import { AdContainer } from "../../../ads";
import PreferredNewsButton from "../preferred-news";

const AuthorAndTimeStamp = ({
  story,
  isDHExclusive,
  isSponsoredStory,
  isBrandspotNoAdStory,
  isBrandspotClientStory,
  isSticky,
  stickyFollowUsBar,
}) => {
  const [showSharingOptions, setShowSharingOptions] = useState(false);
  const [isMobile, setMobile] = useState(true);
  const [isFollowUsSticky, setIsFollowUsSticky] = useState(false);
  const [authorBarHeight, setAuthorBarHeight] = useState(0);

  const tags = story?.tags || [];
  const isPremiumStory = story?.access === "subscription";

  const lastUpdatedDateAndTime = formatStoryDate(story && story["last-published-at"]);

  const shareRef = useRef(null);
  const storyAuthorBarRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const upwardScrollDeltaRef = useRef(0);
  const downwardScrollDeltaRef = useRef(0);
  const authorBarTopRef = useRef(0);
  const authorBarWidthRef = useRef(null);
  const authorBarLeftRef = useRef(null);
  const authorBarHeightRef = useRef(0);
  const adType = useAdType();

  const isAdfree = useSelector((state) => get(state, ["qt", "config", "subscriber"]) === "true");

  const tempHideAd =
    story?.["story-template"] === "brandspot-with-no-ad" ||
    story?.["story-template"] === "exclusive-story-without-ad" ||
    story?.slug?.includes(
      "dhbrandspot-pr/best-crypto-casinos-2025-my-go-to-sites-for-bitcoin-gambling-3470705"
    );

  useEffect(() => {
    setMobile(window.innerWidth < 1024);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSharingOptions && shareRef.current && !shareRef.current.contains(event.target)) {
        setShowSharingOptions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSharingOptions]);

  useEffect(() => {
    if (!stickyFollowUsBar) {
      setIsFollowUsSticky(false);
      setAuthorBarHeight(0);
      authorBarHeightRef.current = 0;
      return;
    }

    const authorBar = storyAuthorBarRef.current;
    if (!authorBar) return;

    const setAuthorBarMetrics = () => {
      const rect = authorBar.getBoundingClientRect();
      authorBarTopRef.current = rect.top + window.scrollY;
      authorBarWidthRef.current = authorBar.offsetWidth || null;
      authorBarLeftRef.current = rect.left;
      const height = authorBar.offsetHeight || 0;
      authorBarHeightRef.current = height;
      setAuthorBarHeight(height);
    };

    setAuthorBarMetrics();
    lastScrollYRef.current = window.scrollY;
    upwardScrollDeltaRef.current = 0;
    downwardScrollDeltaRef.current = 0;

    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        ticking = false;
        const currentScrollY = window.scrollY;
        const scrollDelta = lastScrollYRef.current - currentScrollY;

        if (scrollDelta > 0) {
          upwardScrollDeltaRef.current += scrollDelta;
          downwardScrollDeltaRef.current = 0;
        } else if (scrollDelta < 0) {
          downwardScrollDeltaRef.current += Math.abs(scrollDelta);
        }

        if (downwardScrollDeltaRef.current >= 30) {
          upwardScrollDeltaRef.current = 0;
          downwardScrollDeltaRef.current = 0;
        }

        const isScrollingUp = upwardScrollDeltaRef.current >= 35 && downwardScrollDeltaRef.current < 30;
        lastScrollYRef.current = currentScrollY;

        const barHeight = authorBarHeightRef.current || authorBar.offsetHeight || 0;

        const followUsCard = document.querySelector("#follow-us-card");
        if (followUsCard) {
          const followUsCardTop = followUsCard.getBoundingClientRect().top + 120;
          if (followUsCardTop <= barHeight) {
            setIsFollowUsSticky(false);
            return;
          }
        }

        const taboolaAdWrapper = document.querySelector("#ad-wrapper-taboola");
        if (taboolaAdWrapper) {
          const taboolaAdTop = taboolaAdWrapper.getBoundingClientRect().top + 100;
          if (taboolaAdTop <= barHeight) {
            setIsFollowUsSticky(false);
            return;
          }
        }

        const shouldStick = isScrollingUp && currentScrollY > authorBarTopRef.current - 100;
        setIsFollowUsSticky((prev) => (prev !== shouldStick ? shouldStick : prev));
      });
    };

    const handleResize = () => {
      setAuthorBarMetrics();
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [stickyFollowUsBar]);

  const sharingHandler = async () => {
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: story?.headline,
          text: story?.headline,
          url: story?.url
        });
      } catch (err) { }
      return;
    }
    setShowSharingOptions((prev) => !prev);
  };

  return (
    <div styleName="wrapper">
      {!isSponsoredStory && !isBrandspotNoAdStory && !isBrandspotClientStory && !isDHExclusive && (
        <div className="author-wrapper-id">
          <Authors
            authors={story.authors}
            clazzName="author-name-image"
            lastUpdatedDateAndTime={lastUpdatedDateAndTime}
          />
        </div>
      )}
      {isMobile && !tempHideAd && !isAdfree ? (
        <div styleName="ad-wrapper mobile-top-ad" className="hide-desktop">
          <AdContainer
            storyPageAds={true}
            AdClassName="standard-336x280"
            id={
              adType === "izooto"
                ? "div-gpt-ad-1711971869798-0"
                : "div-gpt-ad-1750399308852-0"
            }
            mobile={
              adType === "izooto"
                ? "DH_MWeb_AT_Top_ym"
                : "DH_MWeb_AT_Top"
            }
            isExternalAdPath={adType === "izooto"}
            cta="inStory"
          />
        </div>
      ) : null}

      {stickyFollowUsBar && isFollowUsSticky && !isMobile ? <div style={{ height: `${authorBarHeight}px` }} /> : null}
      <div
        id="story-author-bar"
        ref={storyAuthorBarRef}
        styleName={`timestamp-share-wrapper ${isSticky ? "sticky" : ""} ${isFollowUsSticky && !isMobile ? "follow-us-fixed" : ""} ${isPremiumStory ? "premium-wrapper" : ""}`}
        style={Object.assign(
          {},
          isPremiumStory ? { background: "var(--premium-bg)" } : {},
          isFollowUsSticky && !isMobile && authorBarWidthRef.current ? { width: `${authorBarWidthRef.current}px`, left: `${authorBarLeftRef.current}px` } : {}
        )}
      >
        <div styleName="author-resize-wrapper-contents">
          <FollowUs />
          <div styleName="comment-share-wrapper">
            <PreferredNewsButton />
            {story["story-template"] !== "live-blog" && (
              <CommentsCount story={story} showCommentText={false} renderPortal={true} />
            )}
            <div styleName="share" ref={shareRef}>
              <button styleName="share-btn" onClick={sharingHandler}>
                <Share color="black" height="20px" width="20px" />
              </button>
              <div styleName="share-options">
                {showSharingOptions && <SocialMediaShare story={story} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

AuthorAndTimeStamp.propTypes = {
  story: object,
  isDHExclusive: bool,
  isSponsoredStory: bool,
  isBrandspotClientStory: bool,
  isBrandspotNoAdStory: bool,
  isSticky: bool,
  stickyFollowUsBar: bool
};

AuthorAndTimeStamp.defaultProps = {
  isDHExclusive: false,
  isSponsoredStory: false,
  isBrandspotClientStory: false,
  isBrandspotNoAdStory: false,
  isSticky: false,
  stickyFollowUsBar: false
};

export default AuthorAndTimeStamp;
